import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MessageSquareWarning, CalendarDays, Users } from 'lucide-react';
import { useMe } from '@/hooks/usePermissions';
import { Role } from '@/types';

interface AppTile {
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  to: string;
  /**
   * Anwendung und Aktion, die diese Kachel voraussetzt.
   *
   * Geprüft wird gegen die aufgelösten Rechte aus `GET /api/me` — also gegen dasselbe,
   * was die Wachen im Backend durchsetzen. Vorher stand hier ein `adminOnly`-Schalter:
   * der versteckte die Benutzerverwaltung, ließ aber die Rezensionen-Kachel für jeden
   * stehen, obwohl ein Depot-Partner dort nichts zu suchen hat.
   *
   * Die Kachel zu verstecken ist reine Ehrlichkeit der Oberfläche, keine Sicherheit —
   * die liegt bei den Wachen. Aber eine Kachel anzubieten, die zu einem 403 führt, ist
   * schlechter als keine.
   */
  requires: { resource: string; action: string };
}

// `satisfies` instead of an `AppTile[]` annotation: the annotation widened the
// key literals to `string`, which the strictly-typed t() (see types/i18next.d.ts)
// rejects. This way the shape is still checked and the literals survive, so a
// typo in a key is caught at compile time.
const apps = [
  {
    titleKey: 'dashboard.apps.reviews.title',
    descriptionKey: 'dashboard.apps.reviews.description',
    icon: <MessageSquareWarning className="h-8 w-8" />,
    to: '/reviews',
    requires: { resource: 'reviews', action: 'view' },
  },
  {
    titleKey: 'dashboard.apps.events.title',
    descriptionKey: 'dashboard.apps.events.description',
    icon: <CalendarDays className="h-8 w-8" />,
    to: '/events',
    requires: { resource: 'events', action: 'view' },
  },
  {
    titleKey: 'dashboard.apps.users.title',
    descriptionKey: 'dashboard.apps.users.description',
    icon: <Users className="h-8 w-8" />,
    to: '/users',
    // Die Benutzerverwaltung hängt an der Rolle, nicht an einer Aktion: hinter
    // /api/users steht `requireAdmin`, und es gibt keine `users`-Anwendung im
    // Rechtekatalog. Deshalb hier die einzige Ausnahme.
    requires: { resource: '__admin__', action: 'admin' },
  },
] as const satisfies readonly AppTile[];

/**
 * Darf dieser Benutzer die Kachel sehen?
 *
 * Als eigene Funktion, weil hier der Fehler wohnt, den man beim Ablesen nicht sieht:
 * fehlen die Rechte noch (die Abfrage läuft), muss die Antwort **nein** sein. Ein
 * `?? true` hätte beim ersten Rendern alle Kacheln gezeigt und sie danach wieder
 * verschwinden lassen — und für einen Moment eine Kachel angeboten, die zu 403 führt.
 */
export function mayOpenTile(
  requires: { resource: string; action: string },
  permissions: Record<string, string[]> | undefined,
  isAdmin: boolean
): boolean {
  if (requires.resource === '__admin__') return isAdmin;
  return permissions?.[requires.resource]?.includes(requires.action) ?? false;
}

export function DashboardPage() {
  const { t } = useTranslation('common');
  // Rolle und Rechte aus GET /api/me, also serverseitig aufgelöst — dieselbe Quelle,
  // die die Wachen im Backend benutzen. Route und API setzen es unabhängig durch;
  // das Filtern hier hält nur das Dashboard ehrlich darüber, was sich öffnen lässt.
  const { data: me } = useMe();
  const isAdmin = me?.role === Role.Admin;

  const visibleApps = apps.filter((app) => mayOpenTile(app.requires, me?.permissions, isAdmin));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">{t('dashboard.title')}</h2>
        <p className="text-muted-foreground">{t('dashboard.description')}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleApps.map((app) => (
          <Link key={app.to} to={app.to} className="group">
            <Card className="transition-colors group-hover:border-primary">
              <CardHeader className="flex flex-row items-center gap-4">
                <div className="rounded-lg bg-muted p-2 text-primary">{app.icon}</div>
                <div className="space-y-1">
                  <CardTitle className="text-lg">{t(app.titleKey)}</CardTitle>
                  <CardDescription>{t(app.descriptionKey)}</CardDescription>
                </div>
              </CardHeader>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
